// Services copy. Organized around the headaches we fix rather than the
// technology we use (Brett's direction, docs/site-review.md). Every result
// quoted below comes from that customer's published case study.
import {
  AppWindow,
  BrainCircuit,
  Cloud,
  Network,
  Smartphone,
} from "lucide-react";

export const servicesPage = {
  eyebrow: "Services",
  title: "We fix the headaches. *The software is just how.*",
  lede: "Nobody calls us wanting an app. They call because something is eating their week — the double entry, the lost tickets, the tools that don't talk, the system nobody wants to touch. Here's what we fix, and how.",
  secondary: "Find your headache",
  fixEyebrow: "What we fix",
  fixTitle: "Sound *familiar?*",
  fixText:
    "These are the problems we get called about most — each one with a company that handed it to us, and what changed.",
  moreTitle: "Got more than one?",
  moreText:
    "Most people do. Bring the whole list — we'll help you sort out what's worth fixing first.",
  pathEyebrow: "How it works",
  pathTitle: "From one meeting to *software your crew uses.*",
  pathText:
    "We don't lead with software. We start with your people and your process, and only then build — so what we deliver gets used.",
  techEyebrow: "The tech, briefly",
  techTitle: "What we build *with.*",
  techText:
    "Whatever the headache needs — and nothing it doesn't. We build on mainstream, well-supported technology, so your software is easy to host, extend and maintain for years, and you're never locked in to us.",
  techToggle: "See the full toolbox",
  faqEyebrow: "Good questions",
  faqTitle: "Before you *book a meeting.*",
};

/**
 * The headaches, in the customer's words. `proof` is one real result from
 * the linked case study (a figure where the study has one, otherwise what we
 * built) — never a made-up stat.
 */
export const headaches = [
  {
    tag: "The subscription pile",
    title: "You're paying for a dozen tools that don't talk.",
    symptoms: [
      "A six-figure software bill, and maybe a third of the features get used",
      "Every department lives in its own app",
    ],
    fix: "One system built around how you actually work, replacing the pile.",
    proof: {
      company: "JRT Mechanical",
      value: "10+ → 1",
      label: "tools replaced, $140k+ a year consolidated",
      href: "/jrt-mechanical-streamlining-operations-with-custom-project-management-software/",
    },
  },
  {
    tag: "Double entry",
    title: "The office re-types what the field already wrote down.",
    symptoms: [
      "Tickets get handled three times — and lost somewhere in between",
      "Billing runs days behind the work",
    ],
    fix: "Enter it once, where the work happens, and let it flow to billing and payroll.",
    proof: {
      company: "Norco Transport",
      value: "80%",
      label: "less manual data entry, same-day invoicing",
      href: "/norco-transport-driving-efficiency-across-every-load/",
    },
  },
  {
    tag: "Outgrown spreadsheets",
    title: "The business runs on one person's spreadsheet.",
    symptoms: [
      "It works — until it breaks, or they're on vacation",
      "Nobody else can use it, and nobody fully trusts the numbers",
    ],
    fix: "Turn the spreadsheet into a reliable tool the whole team can use.",
    proof: {
      company: "Burns Dirt",
      value: "BattleShip",
      label:
        "a breaking Google Sheet became the bidding platform the whole team uses",
      href: "/burns-dirt-bidding-dirt-moving-smarter/",
    },
  },
  {
    tag: "Paper in the field",
    title: "Clipboards, paper forms and checklists that vary by site.",
    symptoms: [
      "Crews spend their time gathering data instead of acting on it",
      "Every site does it a little differently",
    ],
    fix: "Simple field apps — that work without a signal — and one standard everywhere.",
    proof: {
      company: "MJ Electric",
      value: "50%",
      label: "less time compiling equipment lists",
      href: "/mj-electric-optimizing-operations-with-a-digital-equipment-picklist/",
    },
  },
  {
    tag: "The legacy system",
    title: "Old software everyone is afraid to touch.",
    symptoms: [
      "Slow, fragile, and holding the work back",
      "Your product needs software, but you're not a software company",
    ],
    fix: "Modernize it without stopping the business — and stay on as your software team.",
    proof: {
      company: "Somero Enterprises",
      value: "1200%",
      label: "faster 3-D Profiler system",
      href: "/somero-enterprises-inc-advancing-precision-with-a-3d-profiler-system/",
    },
  },
];

/** How an engagement runs, in plain language (Sprint Zero is step 2). */
export const pathSteps = [
  {
    title: "We listen",
    text: "Sixty minutes on what's eating your time, with people who speak contractor, not consultant. We'll sign an NDA if you need one.",
  },
  {
    title: "We come see the work",
    text: "Before any code, we learn the business, walk the process with the people doing it, and find what's worth fixing first.",
  },
  {
    title: "You get a plan and a price",
    text: "A clear roadmap with transparent pricing and the risks worked out — so there are no surprises later.",
  },
  {
    title: "We build, and we stick around",
    text: "Steady check-ins while we build, then support as you grow. It's why 90% of our customers come back.",
  },
];

/** What we build, in plain words; the full toolbox sits behind a toggle. */
export const buildKinds = [
  {
    title: "Office & web apps",
    icon: AppWindow,
    text: "Secure, browser-based systems for the office and the field.",
  },
  {
    title: "Field & mobile apps",
    icon: Smartphone,
    text: "iOS and Android apps that keep working with no signal.",
  },
  {
    title: "Practical AI",
    icon: BrainCircuit,
    text: "AI built into your tools to read paperwork and flag problems.",
  },
  {
    title: "Connecting your systems",
    icon: Network,
    text: "Hook up what you already own so data is entered once.",
  },
  {
    title: "Hosting & support",
    icon: Cloud,
    text: "Cloud hosting, migration and ongoing care after launch.",
  },
];

/** "What to Expect", from the live Schedule a Call page (Contact page). */
export const engagementSteps = [
  {
    title: "Get in Touch",
    text: "Book a call and tell us about your project. We'll sign an NDA if needed.",
  },
  {
    title: "Proposal",
    text: "Get a clear project plan with transparent pricing. Being open with our clients and partners matters to us.",
  },
  {
    title: "Development",
    text: "Our team gets to work, keeping you in the loop as things progress and deadlines near.",
  },
  {
    title: "Confidence",
    text: "You end up with a product you have confidence in using — and that you're proud of.",
  },
];

/** Services FAQ, answered from the live site's own copy. */
export const faqs = [
  {
    title: "Do I need to know what I want built?",
    text: "No. Most people come to us with a problem, not a spec. Tell us what's eating your time and we'll help you figure out what — if anything — is worth building.",
  },
  {
    title: "Do we have to replace the software we already use?",
    text: "No. Sometimes the answer is a new system; often it's connecting what you already own so it finally talks to itself. We build only what you need and will use.",
  },
  {
    title: "Will you sign an NDA?",
    text: "Yes. If you need one, we'll sign it before we dig into the details of your project.",
  },
  {
    title: "How is pricing handled?",
    text: "After the first conversation you get a clear project plan with transparent pricing, so there are no surprises later.",
  },
  {
    title: "Can apps work where there's no signal?",
    text: "They can. We build mobile apps with offline capabilities for remote job sites, so the crew isn't stuck waiting on a signal.",
  },
];
